import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Header from '../components/Header';
import { ChevronRight, RotateCcw, Type, Gauge, BookOpen, Flame, Repeat, Pencil, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import BooksManager from '../components/BooksManager';
import { toast } from 'sonner';

const SPEED_OPTIONS = [0.5, 0.75, 1.0, 1.25, 1.35, 1.5, 1.75, 2.0];

export default function Settings() {
  const navigate = useNavigate();
  const { topics, settings, updateSettings, resetTopics, renameTopic, updateSanskarMantraInsert, reorderMantras } = useApp();
  const yagyaTopic = topics.find(t => t.id === 'daily-yagya');
  const sanskarsTopic = topics.find(t => t.id === 'sanskars');
  const gayatriRepeat = settings.gayatriRepeat || 11;
  const sanskarInsertAfter = settings.sanskarInsertAfter || 'm-y6';

  const [renamingTopic, setRenamingTopic] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [expandedSanskar, setExpandedSanskar] = useState(null);

  const openRename = (topic) => {
    setRenamingTopic(topic);
    setNewTitle(topic.title);
  };

  const saveRename = () => {
    if (!newTitle.trim()) {
      toast.error('शीर्षक खाली नहीं हो सकता।');
      return;
    }
    renameTopic(renamingTopic.id, newTitle.trim());
    toast.success('नाम बदला गया।');
    setRenamingTopic(null);
  };

  return (
    <div className="min-h-screen">
      <Header title="सेटिंग्स" subtitle="Settings" showBack showSettings={false} />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Playback */}
        <section className="bg-white rounded-2xl border border-orange-100 p-5">
          <h3 className="font-bold text-orange-950 devanagari-ui mb-4 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-orange-600" />
            प्लेबैक गति (Speed)
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {SPEED_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => updateSettings({ speed: s })}
                className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  settings.speed === s
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-stone-700 border-orange-200 hover:bg-orange-50'
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </section>

        {/* Font size */}
        <section className="bg-white rounded-2xl border border-orange-100 p-5">
          <h3 className="font-bold text-orange-950 devanagari-ui mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-orange-600" />
            पाठ आकार (Font Size)
          </h3>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-stone-500">14</span>
            <Slider
              value={[settings.fontSize || 18]}
              min={14} max={32} step={1}
              onValueChange={(v) => updateSettings({ fontSize: v[0] })}
            />
            <span className="text-xs text-stone-500">32</span>
          </div>
          <p className="devanagari text-orange-900 mt-3" style={{ fontSize: `${settings.fontSize || 18}px` }}>
            ॐ भूर्भुवः स्वः
          </p>
        </section>

        {/* Yagya Configuration */}
        <section className="bg-white rounded-2xl border border-orange-100 p-5">
          <h3 className="font-bold text-orange-950 devanagari-ui mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-600" />
            यज्ञ क्रम सेटिंग्स
          </h3>

          {/* Gayatri Repeat */}
          <div className="mb-5">
            <Label className="devanagari-ui text-sm flex items-center gap-1.5">
              <Repeat className="w-4 h-4 text-orange-600" />
              गायत्री मन्त्र आहुति बार (कितनी बार दोहराएं)
            </Label>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => updateSettings({ gayatriRepeat: Math.max(1, gayatriRepeat - 1) })}
                className="w-9 h-9 rounded-lg bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold"
              >−</button>
              <Input
                type="number"
                min={1}
                max={108}
                value={gayatriRepeat}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 108) updateSettings({ gayatriRepeat: v });
                }}
                className="text-center font-bold text-lg w-20"
              />
              <button
                onClick={() => updateSettings({ gayatriRepeat: Math.min(108, gayatriRepeat + 1) })}
                className="w-9 h-9 rounded-lg bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold"
              >+</button>
              <span className="text-xs text-stone-500 devanagari-ui">बार</span>
            </div>
            <div className="flex gap-2 mt-2">
              {[3, 11, 21, 27, 54, 108].map(n => (
                <button
                  key={n}
                  onClick={() => updateSettings({ gayatriRepeat: n })}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    gayatriRepeat === n ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-stone-700 border-orange-200 hover:bg-orange-50'
                  }`}
                >{n}</button>
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-2 devanagari-ui">गायत्री आहुति मन्त्र अपने आप {gayatriRepeat} बार चलेगा</p>
          </div>

          {/* Sanskar Mantra Insert Position - per mantra */}
          {sanskarsTopic && yagyaTopic && (
            <div>
              <Label className="devanagari-ui text-sm">
                संस्कार मन्त्र क्रम (हर संस्कार के मन्त्र की position)
              </Label>
              <p className="text-xs text-stone-500 mt-0.5 mb-2 devanagari-ui">
                हर संस्कार के मन्त्र दैनिक यज्ञ के किस मन्त्र के बाद आएंगे — सेट करें (एक बार save करने पर रहेगा)
              </p>
              <div className="space-y-2">
                {(sanskarsTopic.sanskarGroups || []).map((g) => {
                  const groupMantras = sanskarsTopic.mantras.filter(m => m.sanskarGroup === g.id);
                  const isExpanded = expandedSanskar === g.id;
                  return (
                    <div key={g.id} className="border border-orange-200 rounded-lg overflow-hidden bg-orange-50/30">
                      <button
                        onClick={() => setExpandedSanskar(isExpanded ? null : g.id)}
                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-orange-100/60 transition-colors text-left"
                      >
                        <span className="text-sm font-medium text-stone-800 devanagari-ui">{g.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full">{groupMantras.length} मन्त्र</span>
                          <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-2 bg-white">
                          <p className="text-[10px] text-stone-500 devanagari-ui pt-2">↑↓ से क्रम बदलें · dropdown से position सेट करें</p>
                          {groupMantras.map((m, idx) => {
                            // absolute index of m in sanskarsTopic.mantras
                            const absIdx = sanskarsTopic.mantras.findIndex(x => x.id === m.id);
                            const prevSameGroupIdx = idx > 0 ? sanskarsTopic.mantras.findIndex(x => x.id === groupMantras[idx - 1].id) : -1;
                            const nextSameGroupIdx = idx < groupMantras.length - 1 ? sanskarsTopic.mantras.findIndex(x => x.id === groupMantras[idx + 1].id) : -1;
                            return (
                              <div key={m.id} className="flex items-center gap-1.5 pt-1">
                                <div className="flex flex-col">
                                  <button
                                    onClick={() => prevSameGroupIdx >= 0 && reorderMantras('sanskars', absIdx, prevSameGroupIdx)}
                                    disabled={idx === 0}
                                    className="p-0.5 rounded hover:bg-orange-100 disabled:opacity-30 text-orange-700"
                                    aria-label="Up"
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => nextSameGroupIdx >= 0 && reorderMantras('sanskars', absIdx, nextSameGroupIdx)}
                                    disabled={idx === groupMantras.length - 1}
                                    className="p-0.5 rounded hover:bg-orange-100 disabled:opacity-30 text-orange-700"
                                    aria-label="Down"
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </button>
                                </div>
                                <span className="text-xs font-bold text-orange-700 w-5 shrink-0">{idx + 1}.</span>
                                <span className="text-xs text-stone-700 devanagari-ui flex-1 min-w-0 truncate">{m.name}</span>
                                <select
                                  value={m.insertAfterYagyaId || 'm-y6'}
                                  onChange={(e) => updateSanskarMantraInsert(m.id, e.target.value)}
                                  className="text-xs p-1 border border-orange-200 rounded bg-white devanagari-ui max-w-[160px]"
                                >
                                  {yagyaTopic.mantras.filter(ym => !ym.sanskarGroupSource).map((ym, ymIdx) => (
                                    <option key={ym.id} value={ym.id}>{ymIdx + 1}. {ym.name}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Pragya AI - AWGP Books */}
        <BooksManager />

        {/* Modify Topics */}
        <section className="bg-white rounded-2xl border border-orange-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-orange-100">
            <h3 className="font-bold text-orange-950 devanagari-ui flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-600" />
              विषय संपादन (Modify)
            </h3>
            <p className="text-xs text-stone-500 mt-1 devanagari-ui">मन्त्र जोड़ें / हटाएं / क्रम बदलें, ऑडियो/वीडियो जोड़ें</p>
          </div>
          <ul>
            {topics.map(topic => (
              <li key={topic.id} className="flex items-center border-b border-orange-50 last:border-0">
                <button
                  onClick={() => navigate(`/settings/topic/${topic.id}`)}
                  className="flex-1 px-5 py-3 flex items-center gap-3 hover:bg-orange-50 transition-colors text-left"
                >
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${topic.color} shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 devanagari-ui truncate">{topic.title}</p>
                    <p className="text-xs text-stone-500">{topic.mantras.length} {topic.id === 'sanskars' ? 'मन्त्र (' + (topic.sanskarGroups?.length || 0) + ' संस्कार)' : 'मन्त्र'}</p>
                  </div>
                </button>
                <button
                  onClick={() => openRename(topic)}
                  className="p-3 hover:bg-orange-100 text-orange-700 transition-colors"
                  title="नाम बदलें"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate(`/settings/topic/${topic.id}`)}
                  className="p-3 hover:bg-orange-100 text-orange-400 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Reset */}
        <section className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-bold text-red-700 devanagari-ui mb-2 flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            रीसेट
          </h3>
          <p className="text-xs text-stone-600 mb-3 devanagari-ui">सारे मन्त्र एवं क्रम डिफ़ॉल्ट पर वापस आ जाएंगे। अपलोड किए गए मीडिया फ़ाइल्स भी हट जाएंगे।</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium border border-red-200 transition-colors devanagari-ui">
                डिफ़ॉल्ट पर रीसेट करें
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="devanagari-ui">क्या आप निश्चित हैं?</AlertDialogTitle>
                <AlertDialogDescription className="devanagari-ui">इससे सारे परिवर्तन हट जाएंगे। यह कार्य वापस नहीं होगा।</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="devanagari-ui">रद्द करें</AlertDialogCancel>
                <AlertDialogAction onClick={() => { resetTopics(); toast.success('सफलतापूर्वक रीसेट हो गया।'); }} className="bg-red-600 hover:bg-red-700 devanagari-ui">रीसेट करें</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>

        <p className="text-center text-xs text-stone-400 pt-4">कर्मकाण्ड भास्कर · v1.0</p>
      </div>

      {/* Rename Topic Dialog */}
      <Dialog open={!!renamingTopic} onOpenChange={(o) => !o && setRenamingTopic(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="devanagari-ui">शीर्षक बदलें</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-stone-600 devanagari-ui">नया शीर्षक</Label>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="devanagari-ui"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setRenamingTopic(null)} className="devanagari-ui">रद्द करें</Button>
            <Button onClick={saveRename} className="bg-orange-600 hover:bg-orange-700 devanagari-ui">सहेजें</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
